import React, { useState, Suspense } from 'react';
import {
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Modal,
  Paper,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const LocalizationProvider = React.lazy(() => import('@mui/x-date-pickers/LocalizationProvider').then(mod => ({ default: mod.LocalizationProvider })));
const DateTimePicker = React.lazy(() => import('@mui/x-date-pickers/DateTimePicker').then(mod => ({ default: mod.DateTimePicker })));

interface CreatePollDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (pollData: {
    title: string;
    description: string;
    options: string[];
    cause: string;
    endDate: Date;
  }) => Promise<void>;
  causes: { id: string; name: string }[];
}

const CreatePollDialog: React.FC<CreatePollDialogProps> = ({
  open,
  onClose,
  onSubmit,
  causes,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [newOption, setNewOption] = useState('');
  const [selectedCause, setSelectedCause] = useState('');
  const [endDate, setEndDate] = useState<Date | null>(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (title.trim() && options.filter(opt => opt.trim()).length >= 2 && endDate) {
      setIsSubmitting(true);
      try {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        await onSubmit({
          title: title.trim(),
          description: description.trim(),
          options: options.filter(opt => opt.trim()),
          cause: selectedCause,
          endDate: endDate,
        });
        handleReset();
      } catch (error) {
        console.error('Error creating poll:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
    setOptions(['', '']);
    setNewOption('');
    setSelectedCause('');
    setEndDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleDeleteOption = (index: number) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const handleClose = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (onClose) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="create-poll-dialog-title"
      closeAfterTransition
      disablePortal={false}
      container={document.body}
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: '600px' },
          maxHeight: '90vh',
          overflowY: 'auto',
          p: 3,
          outline: 'none',
        }}
        role="dialog"
        aria-modal="true"
      >
        <Typography variant="h6" component="h2" id="create-poll-dialog-title" gutterBottom>
          Create New Poll
        </Typography>
        
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            autoFocus
            inputProps={{
              'aria-label': 'Title',
            }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{
              'aria-label': 'Description',
            }}
          />

          <FormControl fullWidth>
            <InputLabel id="cause-select-label">Select Cause</InputLabel>
            <Select
              labelId="cause-select-label"
              value={selectedCause}
              onChange={(e) => setSelectedCause(e.target.value)}
              label="Select Cause"
              inputProps={{
                'aria-label': 'Select Cause',
              }}
            >
              {causes.map((cause) => (
                <MenuItem key={cause.id} value={cause.id}>
                  {cause.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Options
            </Typography>
            <List>
              {options.map((option, index) => (
                <ListItem key={index} disableGutters>
                  <TextField
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    fullWidth
                    placeholder={`Option ${index + 1}`}
                    required
                    inputProps={{
                      'aria-label': `Option ${index + 1}`,
                    }}
                  />
                  {options.length > 2 && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteOption(index)}
                        color="error"
                        aria-label={`Delete Option ${index + 1}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Add Option"
                fullWidth
                inputProps={{
                  'aria-label': 'Add Option',
                }}
              />
              <Button
                onClick={handleAddOption}
                startIcon={<AddIcon />}
                disabled={!newOption.trim()}
                aria-label="Add Option"
              >
                Add Option
              </Button>
            </Box>
          </Box>

          <Suspense fallback={<div>Loading…</div>}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="End Date"
              value={endDate}
              onChange={(newDate) => setEndDate(newDate)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  inputProps: {
                    'aria-label': 'End Date',
                  }
                }
              }}
            />
          </LocalizationProvider>
          </Suspense>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button 
              onClick={handleReset}
              aria-label="Cancel"
            >
              Cancel
            </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !title.trim() ||
            options.filter((opt) => opt.trim()).length < 2 ||
            !selectedCause ||
            !endDate ||
            isSubmitting
          }
              aria-label="Create"
        >
          Create
        </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default CreatePollDialog; 